import React, { useEffect } from 'react'

import { graphql } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import Breadcrumbs from '../../components/core/Breadcrumbs'
import Layout from '../../components/core/layout'
import SEO from '../../components/core/seo'
import AccreditedYouBet from '../../components/cross-template-statics/AccreditedYouBet'
import EmployerTestimonials from '../../components/cross-template-statics/EmployerTestimonials'
import HeroInternal from '../../components/elements/HeroInternal'
import ComposedFormHireUMA from '../../components/forms/ComposedFormHireUMA'
import ChevronBackground from '../../components/reusables/ChevronBackground'
import { personaEnums } from '../../enums'
import usePersona from '../../hooks/usePersona'
import { colors, space } from '../../style/design-system'
import { gatsbyImage as ptGatsbyImage } from '../../types/props'
import { studentInfo } from '../../utils/static-data/studentInfo'

const OurGraduates = ({ data, className, pageContext }) => {
  const { resetPersona, setPersona } = usePersona()
  useEffect(() => {
    setPersona(personaEnums.personas.EMPLOYER)
    return resetPersona
  }, [resetPersona, setPersona])

  return (
    <Layout>
      <SEO
        title="Our Graduates"
        description="New UMA graduates are filled with determination, ready to enhance the healthcare field. Request to connect with them here."
      />

      <div className={className}>
        <HeroInternal
          backgroundImage={{
            image: data.heroImgLg.childImageSharp.gatsbyImageData,
            objectPosition: '0 5px',
          }}
          backgroundImageMobile={{
            image: data.heroImgLg.childImageSharp.gatsbyImageData,
            objectPosition: 'center top',
          }}
          borderColor={colors.dev.yellow.e(0.75)}
          contentLg="Meet Our <span class='nowrap'>Job-Ready</span> Candidates"
        />
        <main>
          <div className="container text-lg-center">
            <Breadcrumbs pageContext={pageContext} />
          </div>

          <div className="container d-lg-none">
            <h1 className="mt-0 mb-9">
              Meet Our
              <span className="nowrap">Job-Ready</span> Candidates
            </h1>
          </div>

          <div className="container mb-9">
            <div className="row">
              <div className="col-12 col-xl-8 offset-xl-2">
                <h2 className="mt-0">Our Graduates</h2>
                <p>
                  We champion our students’ success and support them along their path to a meaningful healthcare career.
                  Ultimate Medical Academy delivers a strong curriculum that balances field-specific coursework with
                  technology and soft skills. Our culture of support helps to develop graduates with the employable
                  skills and training needed to join the allied healthcare workforce.
                </p>
                <p>
                  UMA is proud of its {studentInfo.grads.number} alumni, who have prepared themselves for careers in
                  healthcare facilities, medical offices, hospitals and practices across the United States. We are
                  equally proud to be a trusted resource for our longstanding employer partners, providing skilled
                  healthcare talent for medical organizations of all sizes.
                </p>
              </div>
            </div>
          </div>

          <ChevronBackground className="text-white-force py-9" backgroundColor="blue" direction="north">
            <div className="container">
              <div className="row">
                <div className="col-12 col-xl-8 offset-xl-2">
                  <h2 className="mt-0">The UMA Footprint</h2>
                  <p>
                    Our growing network of {studentInfo.grads.number} alumni are represented across the U.S., allowing
                    us to fulfill your healthcare staffing needs regionally.
                  </p>
                  <StaticImage
                    src="../../assets/images/our-graduates/map-of-uma-grads.png"
                    alt="A map of where UMA graduates live."
                    placeholder="none"
                    quality="40"
                  />
                </div>
              </div>
            </div>
          </ChevronBackground>

          <div className="container list-style-ascii-chevron my-9">
            <div className="row">
              <div className="col-12 col-xl-8 offset-xl-2">
                <p>
                  UMA grads have the training and drive to succeed in today’s healthcare industry. Here’s what
                  differentiates UMA graduates in the healthcare field:
                </p>

                <div className="row my-9 align-items-center">
                  <div className="col-md-6">
                    <div className="video-wrapper">
                      <StaticImage
                        src="../../assets/images/bricks/interview-ready.jpg"
                        alt="An interview between a man and a woman."
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h3 className="mt-6">
                      <span>Interview ready candidates</span>
                    </h3>
                    <p>
                      Our students’ career-readiness preparation begins before they graduate. UMA’s Career Services team
                      will offer assistance writing resumes, practicing interviewing techniques, and will provide steady
                      guidance throughout their entire job search process.
                    </p>
                  </div>
                </div>

                <div className="row my-9 align-items-center">
                  <div className="col-md-6">
                    <div className="video-wrapper">
                      <StaticImage
                        src="../../assets/images/bricks/industry-knowledge.jpg"
                        alt="Two medical professionals review information."
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h3 className="mt-6">
                      <span>Industry knowledge</span>
                    </h3>
                    <p>
                      Our graduates have completed fundamental healthcare courses that give them a general understanding
                      of the healthcare industry. A majority of the programs feature course topics such as:
                    </p>
                    <ul>
                      <li>Medical terminology</li>
                      <li>Anatomy and physiology</li>
                      <li>HIPAA compliance courses</li>
                      <li>Critical thinking and problem-solving</li>
                    </ul>
                    <p>
                      Select programs also include externship or practicum experience for additional real-world
                      training.
                    </p>
                  </div>
                </div>

                <div className="row my-9 align-items-center">
                  <div className="col-md-6">
                    <div className="video-wrapper">
                      <StaticImage
                        src="../../assets/images/bricks/technical-and-communication-skills.jpg"
                        alt="A healthcare professional assists a mother and her daughter."
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h3 className="mt-6">
                      <span>Technical and communication skills</span>
                    </h3>
                    <p>
                      To be prepared for the today’s healthcare workplace, graduates need a combination of technical
                      knowledge and good communication skills. Depending on the program, students will also take a
                      variety of general education courses that will enhance their background such as English
                      Composition or a course on interpersonal communication.
                    </p>
                  </div>
                </div>

                <div className="row my-9 align-items-center">
                  <div className="col-md-6">
                    <div className="video-wrapper">
                      <StaticImage
                        src="../../assets/images/bricks/comfort-with-technology.jpg"
                        alt="A healthcare professional working on a computer."
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h3 className="mt-6">
                      <span>Comfort with technology</span>
                    </h3>
                    <p>
                      With a majority of our student body completing online programs, our graduates have the technology
                      skills and experience for today’s increasingly electronic healthcare workplace.
                    </p>
                  </div>
                </div>

                <p>
                  Find out how UMA can help fill your open position with our well-trained graduates &mdash; at no fee to
                  you.
                </p>
              </div>
            </div>
          </div>

          <div className="section--form">
            <div className="container py-9">
              <div className="row">
                <div className="col-12 col-xl-8 offset-xl-2">
                  <ComposedFormHireUMA makeFullWidth />
                </div>
              </div>
            </div>
          </div>

          <div className="section--testimonials">
            <EmployerTestimonials />
          </div>

          <AccreditedYouBet />
        </main>
      </div>
    </Layout>
  )
}

OurGraduates.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    heroImgLg: ptGatsbyImage.isRequired,
  }).isRequired,
  pageContext: PropTypes.shape({}).isRequired,
}

OurGraduates.defaultProps = {
  className: '',
}

export default styled(OurGraduates)`
  h3 {
    * {
      display: inline-block;
      vertical-align: baseline;
    }

    svg {
      margin-right: ${space.b};
    }
  }

  .section--form {
    background-color: ${colors.dev.gray.h()};
  }
`

export const query = graphql`
  {
    heroImgLg: file(absolutePath: { regex: "/our-graduates/hero-lg/" }) {
      childImageSharp {
        gatsbyImageData(width: 1112, quality: 90, placeholder: NONE, layout: FIXED)
      }
    }
  }
`
